import { RadioGroup } from '@progress/kendo-react-inputs';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useAuth from 'src/hooks/useAuth';
import useBoolean from 'src/hooks/useBoolean';
import useDownloadResultsApi from 'src/services/useDownloadResultsApi';
import { LastImportDetailsResponse } from 'src/services/useLastImportDetailsApi';
import { MAX_EXCEL_ROWS } from './Export';

function base64ToFile(base64String: string, mimeType: string, fileName: string) {
  const data = `data:${mimeType};base64,${base64String}`;
  const link = document.createElement('a');
  link.href = data;
  link.download = fileName;
  link.click();
}

const Results = ({ lastImportDetails }: { lastImportDetails: LastImportDetailsResponse }) => {
  const { value: isResultsModalOpen, toggle: toggleResultsModal } = useBoolean();
  const radioButtonData = [
    { label: '.xls', value: 'xls' },
    { label: '.txt (tab delimited)', value: 'txt' },
  ];
  const [fileType, setFileType] = useState<'xls' | 'txt'>('xls');

  const formattedTime = moment(lastImportDetails?.importOn).format('dddd, MMMM DD, YYYY h:mm A');
  const { execute, isLoading } = useDownloadResultsApi();
  const resultExceptionsFileName = `${lastImportDetails?.importExceptionFile?.fileName}.${
    fileType === 'txt' ? 'txt' : 'xls'
  }`;

  const handleDownload = async () => {
    const data = await execute({ importId: lastImportDetails.importId, fileType });
    base64ToFile(
      data.data,
      fileType === 'txt' ? 'text/plain' : 'application/vnd.ms-excel',
      resultExceptionsFileName
    );
  };

  const { user } = useAuth();
  const { t } = useTranslation();

  const isUserLastUser = +user?.ClientId === lastImportDetails?.importByUserId;

  const isExcelRowExceed = useMemo(
    () => lastImportDetails?.importExceptionFile?.failedCount > MAX_EXCEL_ROWS,
    [lastImportDetails?.importExceptionFile?.failedCount]
  );

  useEffect(() => {
    if (fileType === 'xls' && isExcelRowExceed) setFileType('txt');
  }, [fileType, isExcelRowExceed]);
  return (
    <>
      {lastImportDetails?.importBy && (
        <div className="mt-2">
          <label className="font-bold text-xs">{t('Last Import Result for Company')}</label>
          <div className="[&>p]:text-xs p-4 mt-2 border bg-[#e9f5f8] border-[#0e80b4]">
            {!!lastImportDetails?.importId && (
              <h6
                data-tooltip-id="global-tooltip"
                data-tooltip-content={lastImportDetails?.uploadedFileName}
                className="text-xs font-bold max-w-[330px] text-ellipsis"
              >
                {lastImportDetails?.uploadedFileName}
              </h6>
            )}
            <p className="opacity-40 mb-3">
              {`Last imported by
            ${
              lastImportDetails?.importId
                ? `${lastImportDetails?.importBy}  on ${formattedTime} PT`
                : `Auto Import on ${formattedTime} PT`
            }
            `}
            </p>
            {!!lastImportDetails?.importId && (
              <p
                className="border-b border-[#0e80b4] font-semibold text-[#0e80b4] inline-block cursor-pointer"
                onClick={toggleResultsModal}
              >
                View Results
              </p>
            )}
          </div>
        </div>
      )}
      <Dialog
        width="500px"
        id="modal"
        title="Results"
        open={isResultsModalOpen}
        onClose={toggleResultsModal}
      >
        <ul className="list-none [&>li>span]:font-semibold">
          <li>
            Success :{' '}
            <span className="text-success">
              {lastImportDetails?.importExceptionFile?.successCount}
            </span>{' '}
            rows
          </li>
          <li>
            Failed :{' '}
            <span className="text-error">
              {lastImportDetails?.importExceptionFile?.failedCount}
            </span>{' '}
            rows
          </li>
          {lastImportDetails?.importExceptionFile?.failedCount > 0 && isUserLastUser && (
            <li className="my-5">
              Error(s) found during import. Please download the file below to view and fix errors.
            </li>
          )}
        </ul>
        {lastImportDetails?.importExceptionFile?.failedCount > 0 && isUserLastUser && (
          <>
            <div className="mb-2">
              <PopUp
                show={isExcelRowExceed}
                content={
                  <PopUpContentWrapper
                    type="Error"
                    contents={['Exceptions file would exceed row limit in excel']}
                  />
                }
                popupAlign={{ horizontal: 'left', vertical: 'bottom' }}
                anchorAlign={{ horizontal: 'left', vertical: 'center' }}
              >
                <label className="font-bold text-xs mb-2 block">File Type</label>
                <RadioGroup
                  data={radioButtonData}
                  layout="horizontal"
                  className="text-xs"
                  onChange={(e) => setFileType(e.value)}
                  value={fileType}
                  disabled={isExcelRowExceed}
                />
              </PopUp>
            </div>
            <div className="p-4 border-dashed border border-gray-300 flex justify-between">
              <div className="[&>p]:text-xs ">
                <p className="font-bold">{resultExceptionsFileName}</p>
              </div>
              <Button color="info" onClick={handleDownload} loading={isLoading}>
                Download
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
};

export default Results;
